import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { FileText, Mail, MessageCircle, Calendar, User, MapPin, Music, DollarSign, Filter, SortAsc, SortDesc, Printer, BarChart3, Users, Building2, Clock, ChevronDown, ChevronUp, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

// Función para normalizar texto (sin tildes, minúsculas)
const normalizeText = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
};

// Función para formatear fecha
const formatDate = (dateStr) => {
  if (!dateStr) return 'Sin fecha';
  try {
    return new Date(dateStr).toLocaleDateString('es-ES', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

// Función para formatear moneda
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount);
};

// Función para obtener el caché efectivo de un evento
// Prioridad: cache_acordado > presupuesto_total > suma de servicios contratados
const getEffectiveCache = (event) => {
  // 1. Si tiene cache_acordado, usarlo
  if (event.cache_acordado && parseFloat(event.cache_acordado) > 0) {
    return parseFloat(event.cache_acordado);
  }
  
  // 2. Si tiene presupuesto_total, usarlo
  if (event.presupuesto_total && parseFloat(event.presupuesto_total) > 0) {
    return parseFloat(event.presupuesto_total);
  }
  
  // 3. Calcular desde servicios contratados
  if (event.servicios_contratados && Array.isArray(event.servicios_contratados)) {
    const total = event.servicios_contratados
      .filter(s => s.contratado)
      .reduce((sum, s) => {
        const precio = s.precio_personalizado ?? s.precio_final ?? s.precio_base ?? 0;
        const cantidad = s.cantidad || 1;
        return sum + (precio * cantidad);
      }, 0);
    if (total > 0) return total;
  }
  
  return 0;
};

// Devuelve los asistentes presentes en el evento (Asistente 1 y/o 2)
const getAsistentes = (event) => {
  const a = [];
  if (event.nombre_asistente) a.push(event.nombre_asistente);
  if (event.nombre_asistente2) a.push(event.nombre_asistente2);
  return a;
};

// Clave para sessionStorage
const INFORMES_FILTERS_KEY = 'informes_filters';

// Función para cargar filtros guardados de sessionStorage
const loadSavedFilters = () => {
  try {
    const saved = sessionStorage.getItem(INFORMES_FILTERS_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.warn('Error loading saved filters:', e);
  }
  return null;
};

export const InformesView = ({ playlists = [], onSelectEvent }) => {
  // Cargar filtros guardados al inicializar
  const savedFilters = useMemo(() => loadSavedFilters(), []);

  // Estado de filtros - inicializar desde sessionStorage si existe
  const [activeTab, setActiveTab] = useState(savedFilters?.activeTab || 'general');
  const [searchTerm, setSearchTerm] = useState(savedFilters?.searchTerm || '');
  const [selectedDJ, setSelectedDJ] = useState(savedFilters?.selectedDJ || '');
  const [selectedVenue, setSelectedVenue] = useState(savedFilters?.selectedVenue || '');
  const [selectedService, setSelectedService] = useState(savedFilters?.selectedService || '');
  const [dateFrom, setDateFrom] = useState(savedFilters?.dateFrom || '');
  const [dateTo, setDateTo] = useState(savedFilters?.dateTo || '');
  const [hidePastEvents, setHidePastEvents] = useState(savedFilters?.hidePastEvents ?? false);
  const [sortOrder, setSortOrder] = useState(savedFilters?.sortOrder || 'desc'); // 'asc' o 'desc'
  const [expandedResults, setExpandedResults] = useState(savedFilters?.expandedResults ?? true);
  const [hideFinancials, setHideFinancials] = useState(savedFilters?.hideFinancials ?? false); // Ocultar info económica en informes

  // Guardar filtros en sessionStorage cuando cambien
  useEffect(() => {
    const filtersToSave = {
      activeTab,
      searchTerm,
      selectedDJ,
      selectedVenue,
      selectedService,
      dateFrom,
      dateTo,
      hidePastEvents,
      sortOrder,
      expandedResults,
      hideFinancials
    };
    try {
      sessionStorage.setItem(INFORMES_FILTERS_KEY, JSON.stringify(filtersToSave));
    } catch (e) {
      console.warn('Error saving filters:', e);
    }
  }, [activeTab, searchTerm, selectedDJ, selectedVenue, selectedService, dateFrom, dateTo, hidePastEvents, sortOrder, expandedResults, hideFinancials]);

  // Extraer listas únicas para filtros — incluye DJ titular + asistentes
  // (un asistente debe poder filtrarse en "Por DJ" para ver eventos donde participó)
  const uniqueDJs = useMemo(() => {
    const djs = new Set();
    playlists.forEach(p => {
      if (p.nombre_dj) djs.add(p.nombre_dj);
      if (p.nombre_asistente) djs.add(p.nombre_asistente);
      if (p.nombre_asistente2) djs.add(p.nombre_asistente2);
    });
    return Array.from(djs).sort();
  }, [playlists]);

  const uniqueVenues = useMemo(() => {
    const venues = new Set();
    playlists.forEach(p => {
      if (p.lugar_evento) venues.add(p.lugar_evento);
    });
    return Array.from(venues).sort();
  }, [playlists]);

  const uniqueServices = useMemo(() => {
    const services = new Set();
    playlists.forEach(p => {
      if (p.servicios_contratados && Array.isArray(p.servicios_contratados)) {
        p.servicios_contratados.forEach(s => {
          if (s.contratado && s.servicio) {
            services.add(s.servicio);
          }
        });
      }
    });
    return Array.from(services).sort();
  }, [playlists]);

  // Filtrar eventos según criterios
  const filteredEvents = useMemo(() => {
    let filtered = [...playlists];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Filtro de texto general
    if (searchTerm) {
      const search = normalizeText(searchTerm);
      filtered = filtered.filter(p => {
        const matchNombre = normalizeText(p.nombre_novios).includes(search);
        const matchLugar = normalizeText(p.lugar_evento).includes(search);
        const matchDJ = normalizeText(p.nombre_dj).includes(search);
        const matchAsistente = normalizeText(p.nombre_asistente).includes(search);
        const matchAsistente2 = normalizeText(p.nombre_asistente2).includes(search);
        const matchServicio = p.servicios_contratados?.some(s => 
          s.contratado && normalizeText(s.servicio).includes(search)
        );
        return matchNombre || matchLugar || matchDJ || matchAsistente || matchAsistente2 || matchServicio;
      });
    }

    // Filtro por DJ — incluye participación como titular o asistente 1/2
    if (selectedDJ) {
      filtered = filtered.filter(p =>
        p.nombre_dj === selectedDJ ||
        p.nombre_asistente === selectedDJ ||
        p.nombre_asistente2 === selectedDJ
      );
    }

    // Filtro por Venue
    if (selectedVenue) {
      filtered = filtered.filter(p => p.lugar_evento === selectedVenue);
    }

    // Filtro por Servicio
    if (selectedService) {
      filtered = filtered.filter(p => 
        p.servicios_contratados?.some(s => s.contratado && s.servicio === selectedService)
      );
    }

    // Filtro por fecha desde
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      filtered = filtered.filter(p => {
        if (!p.fecha_evento) return false;
        return new Date(p.fecha_evento) >= fromDate;
      });
    }

    // Filtro por fecha hasta
    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(p => {
        if (!p.fecha_evento) return false;
        return new Date(p.fecha_evento) <= toDate;
      });
    }

    // Ocultar eventos pasados
    if (hidePastEvents) {
      filtered = filtered.filter(p => {
        if (!p.fecha_evento) return true;
        return new Date(p.fecha_evento) >= today;
      });
    }

    // Ordenar por fecha
    filtered.sort((a, b) => {
      const dateA = a.fecha_evento ? new Date(a.fecha_evento) : new Date(0);
      const dateB = b.fecha_evento ? new Date(b.fecha_evento) : new Date(0);
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });

    return filtered;
  }, [playlists, searchTerm, selectedDJ, selectedVenue, selectedService, dateFrom, dateTo, hidePastEvents, sortOrder]);

  // Calcular estadísticas
  const stats = useMemo(() => {
    const total = filteredEvents.length;
    const totalCache = filteredEvents.reduce((sum, p) => sum + getEffectiveCache(p), 0);
    const confirmados = filteredEvents.filter(p => p.estado === 'confirmado').length;
    const pendientes = filteredEvents.filter(p => p.estado === 'pendiente').length;
    const pasados = filteredEvents.filter(p => {
      if (!p.fecha_evento) return false;
      return new Date(p.fecha_evento) < new Date();
    }).length;
    const futuros = total - pasados;

    // Servicios más contratados
    const serviciosCount = {};
    filteredEvents.forEach(p => {
      p.servicios_contratados?.forEach(s => {
        if (s.contratado && s.servicio) {
          serviciosCount[s.servicio] = (serviciosCount[s.servicio] || 0) + 1;
        }
      });
    });
    const topServicios = Object.entries(serviciosCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    return { total, totalCache, confirmados, pendientes, pasados, futuros, topServicios };
  }, [filteredEvents]);

  // Resumen del equipo: por cada persona, nº de eventos como titular vs asistente.
  // Se calcula sobre filteredEvents (que cuando NO hay DJ seleccionado contiene
  // todos los eventos que cumplen el resto de filtros). Útil para repartir trabajo
  // y calcular pagos al equipo.
  const teamStats = useMemo(() => {
    const map = {};
    const add = (name, role, cache) => {
      if (!name) return;
      if (!map[name]) map[name] = { name, titular: 0, asistente: 0, total: 0, facturacionTitular: 0 };
      map[name][role] += 1;
      map[name].total += 1;
      if (role === 'titular') map[name].facturacionTitular += cache || 0;
    };
    filteredEvents.forEach(e => {
      add(e.nombre_dj, 'titular', getEffectiveCache(e));
      add(e.nombre_asistente, 'asistente', 0);
      add(e.nombre_asistente2, 'asistente', 0);
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [filteredEvents]);

  // Desglose de roles del DJ seleccionado (titular vs asistente) en el período.
  const selectedDJRoleStats = useMemo(() => {
    if (!selectedDJ) return null;
    let titular = 0, asistente = 0;
    filteredEvents.forEach(e => {
      if (e.nombre_dj === selectedDJ) titular += 1;
      else if (e.nombre_asistente === selectedDJ || e.nombre_asistente2 === selectedDJ) asistente += 1;
    });
    return { titular, asistente, total: titular + asistente };
  }, [selectedDJ, filteredEvents]);

  // Limpiar filtros
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedDJ('');
    setSelectedVenue('');
    setSelectedService('');
    setDateFrom('');
    setDateTo('');
    setHidePastEvents(false);
    // Limpiar también el sessionStorage
    try {
      sessionStorage.removeItem(INFORMES_FILTERS_KEY);
    } catch (e) {
      console.warn('Error clearing filters from storage:', e);
    }
  };

  // Generar contenido para WhatsApp
  const generateWhatsAppContent = useCallback(() => {
    const lines = [];
    lines.push('📊 *INFORME DE EVENTOS*');
    lines.push('━━━━━━━━━━━━━━━━━━━━');
    
    // Filtros aplicados
    const filtros = [];
    if (selectedDJ) filtros.push(`🎧 DJ: ${selectedDJ}`);
    if (selectedVenue) filtros.push(`📍 Lugar: ${selectedVenue}`);
    if (selectedService) filtros.push(`🎯 Servicio: ${selectedService}`);
    if (dateFrom || dateTo) {
      const periodo = `${dateFrom ? formatDate(dateFrom) : '...'} → ${dateTo ? formatDate(dateTo) : '...'}`;
      filtros.push(`📅 Período: ${periodo}`);
    }
    if (filtros.length > 0) {
      lines.push('');
      lines.push('*Filtros:*');
      filtros.forEach(f => lines.push(f));
    }
    
    lines.push('');
    lines.push(`📈 *Resumen:* ${stats.total} eventos`);
    if (!hideFinancials) {
      lines.push(`💰 *Total facturación:* ${formatCurrency(stats.totalCache)}`);
    }
    lines.push(`✅ Confirmados: ${stats.confirmados}`);
    lines.push(`⏳ Pendientes: ${stats.pendientes}`);
    lines.push('');
    lines.push('━━━━━━━━━━━━━━━━━━━━');
    lines.push('*DETALLE DE EVENTOS:*');
    lines.push('');

    filteredEvents.slice(0, 20).forEach((event, idx) => {
      lines.push(`*${idx + 1}. ${event.nombre_novios}*`);
      lines.push(`   📅 ${formatDate(event.fecha_evento)}`);
      lines.push(`   📍 ${event.lugar_evento || 'Sin ubicación'}`);
      if (event.nombre_dj) lines.push(`   🎧 ${event.nombre_dj}`);
      const asisWa = getAsistentes(event);
      if (asisWa.length > 0) lines.push(`   👥 Asist.: ${asisWa.join(', ')}`);
      if (!hideFinancials) {
        lines.push(`   💰 ${formatCurrency(getEffectiveCache(event))}`);
      }
      
      // Servicios contratados
      const servicios = event.servicios_contratados?.filter(s => s.contratado).map(s => s.servicio) || [];
      if (servicios.length > 0) {
        lines.push(`   🎯 ${servicios.join(', ')}`);
      }
      lines.push('');
    });

    if (filteredEvents.length > 20) {
      lines.push(`... y ${filteredEvents.length - 20} eventos más`);
    }

    lines.push('━━━━━━━━━━━━━━━━━━━━');
    lines.push('_Luis Herrera Eventos_');
    lines.push(`_Generado: ${new Date().toLocaleString('es-ES')}_`);

    return lines.join('\n');
  }, [filteredEvents, stats, selectedDJ, selectedVenue, selectedService, dateFrom, dateTo, hideFinancials]);

  // Compartir por WhatsApp
  const shareWhatsApp = () => {
    const content = generateWhatsAppContent();
    const encoded = encodeURIComponent(content);
    window.open(`https://wa.me/?text=${encoded}`, '_blank');
    toast.success('Abriendo WhatsApp...');
  };

  // Generar contenido para Email (HTML)
  const generateEmailContent = useCallback(() => {
    const filtros = [];
    if (selectedDJ) filtros.push(`DJ: ${selectedDJ}`);
    if (selectedVenue) filtros.push(`Lugar: ${selectedVenue}`);
    if (selectedService) filtros.push(`Servicio: ${selectedService}`);
    if (dateFrom || dateTo) {
      filtros.push(`Período: ${dateFrom || '...'} → ${dateTo || '...'}`);
    }

    const subject = `Informe de Eventos - Luis Herrera Eventos${filtros.length > 0 ? ` (${filtros.join(', ')})` : ''}`;
    
    let body = `
INFORME DE EVENTOS
==================

`;
    if (filtros.length > 0) {
      body += `Filtros aplicados: ${filtros.join(' | ')}\n\n`;
    }

    body += `RESUMEN:
• Total eventos: ${stats.total}`;
    if (!hideFinancials) {
      body += `
• Facturación total: ${formatCurrency(stats.totalCache)}`;
    }
    body += `
• Confirmados: ${stats.confirmados}
• Pendientes: ${stats.pendientes}

==================
DETALLE DE EVENTOS:
==================

`;

    filteredEvents.forEach((event, idx) => {
      body += `${idx + 1}. ${event.nombre_novios}
   Fecha: ${formatDate(event.fecha_evento)}
   Lugar: ${event.lugar_evento || 'Sin ubicación'}
   DJ: ${event.nombre_dj || 'Sin asignar'}`;
      const asisEmail = getAsistentes(event);
      if (asisEmail.length > 0) {
        body += `
   Asistentes: ${asisEmail.join(', ')}`;
      }
      if (!hideFinancials) {
        body += `
   Caché: ${formatCurrency(getEffectiveCache(event))}`;
      }
      body += `
   Estado: ${event.estado || 'Sin estado'}
`;
      const servicios = event.servicios_contratados?.filter(s => s.contratado).map(s => s.servicio) || [];
      if (servicios.length > 0) {
        body += `   Servicios: ${servicios.join(', ')}\n`;
      }
      body += '\n';
    });

    body += `
==================
Generado: ${new Date().toLocaleString('es-ES')}
Luis Herrera Eventos
`;

    return { subject, body };
  }, [filteredEvents, stats, selectedDJ, selectedVenue, selectedService, dateFrom, dateTo, hideFinancials]);

  // Enviar por Email
  const sendEmail = () => {
    const { subject, body } = generateEmailContent();
    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
    toast.success('Abriendo cliente de correo...');
  };

  // Generar e imprimir PDF
  const printReport = () => {
    // Crear contenido HTML para imprimir
    const printWindow = window.open('', '_blank');
    
    const filtros = [];
    if (selectedDJ) filtros.push(`DJ: ${selectedDJ}`);
    if (selectedVenue) filtros.push(`Lugar: ${selectedVenue}`);
    if (selectedService) filtros.push(`Servicio: ${selectedService}`);
    if (dateFrom || dateTo) {
      filtros.push(`Período: ${formatDate(dateFrom) || '...'} → ${formatDate(dateTo) || '...'}`);
    }

    // Determinar número de columnas del grid de estadísticas
    const statsColumns = hideFinancials ? 3 : 4;
    
    // Generar cabeceras de tabla según hideFinancials
    const tableHeaders = hideFinancials ? `
        <th style="width: 5%">#</th>
        <th style="width: 25%">Evento</th>
        <th style="width: 15%">Fecha</th>
        <th style="width: 20%">Lugar</th>
        <th style="width: 12%">DJ / Asist.</th>
        <th style="width: 10%">Estado</th>
        <th style="width: 13%">Servicios</th>
    ` : `
        <th style="width: 5%">#</th>
        <th style="width: 20%">Evento</th>
        <th style="width: 12%">Fecha</th>
        <th style="width: 18%">Lugar</th>
        <th style="width: 12%">DJ / Asist.</th>
        <th style="width: 10%">Caché</th>
        <th style="width: 10%">Estado</th>
        <th style="width: 13%">Servicios</th>
    `;

    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Informe de Eventos - Luis Herrera Eventos</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { 
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      padding: 20px;
      color: #333;
      line-height: 1.5;
    }
    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 3px solid #0ea5e9;
      padding-bottom: 15px;
      margin-bottom: 20px;
    }
    .logo-section {
      display: flex;
      align-items: center;
      gap: 15px;
    }
    .logo {
      width: 60px;
      height: 60px;
      object-fit: contain;
    }
    .company-name {
      font-size: 24px;
      font-weight: bold;
      color: #0ea5e9;
    }
    .report-title {
      font-size: 18px;
      color: #666;
    }
    .date {
      font-size: 12px;
      color: #888;
    }
    .filters {
      background: #f8fafc;
      padding: 10px 15px;
      border-radius: 8px;
      margin-bottom: 20px;
      font-size: 13px;
    }
    .filters strong { color: #0ea5e9; }
    .stats-grid {
      display: grid;
      grid-template-columns: repeat(${statsColumns}, 1fr);
      gap: 15px;
      margin-bottom: 25px;
    }
    .stat-card {
      background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
      color: white;
      padding: 15px;
      border-radius: 10px;
      text-align: center;
    }
    .stat-card.secondary {
      background: linear-gradient(135deg, #64748b 0%, #475569 100%);
    }
    .stat-value { font-size: 28px; font-weight: bold; }
    .stat-label { font-size: 12px; opacity: 0.9; }
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 12px;
    }
    th {
      background: #1e293b;
      color: white;
      padding: 12px 8px;
      text-align: left;
      font-weight: 600;
    }
    td {
      padding: 10px 8px;
      border-bottom: 1px solid #e2e8f0;
      vertical-align: top;
    }
    tr:nth-child(even) { background: #f8fafc; }
    tr:hover { background: #e0f2fe; }
    .event-name { font-weight: 600; color: #0f172a; }
    .event-services { 
      font-size: 11px; 
      color: #0ea5e9;
      margin-top: 4px;
    }
    .status {
      display: inline-block;
      padding: 3px 8px;
      border-radius: 12px;
      font-size: 10px;
      font-weight: 600;
    }
    .status.confirmado { background: #dcfce7; color: #166534; }
    .status.pendiente { background: #fef3c7; color: #92400e; }
    .status.cancelado { background: #fee2e2; color: #991b1b; }
    .footer {
      margin-top: 30px;
      padding-top: 15px;
      border-top: 2px solid #e2e8f0;
      text-align: center;
      font-size: 11px;
      color: #64748b;
    }
    @media print {
      body { padding: 10px; }
      .stat-card { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      th { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="logo-section">
      <img src="/logo.png" alt="Logo" class="logo" onerror="this.style.display='none'">
      <div>
        <div class="company-name">Luis Herrera Eventos</div>
        <div class="report-title">Informe de Eventos</div>
      </div>
    </div>
    <div class="date">
      Generado: ${new Date().toLocaleString('es-ES')}
    </div>
  </div>

  ${filtros.length > 0 ? `
  <div class="filters">
    <strong>Filtros aplicados:</strong> ${filtros.join(' • ')}
  </div>
  ` : ''}

  <div class="stats-grid">
    <div class="stat-card">
      <div class="stat-value">${stats.total}</div>
      <div class="stat-label">Total Eventos</div>
    </div>
    ${!hideFinancials ? `
    <div class="stat-card">
      <div class="stat-value">${formatCurrency(stats.totalCache)}</div>
      <div class="stat-label">Facturación Total</div>
    </div>
    ` : ''}
    <div class="stat-card secondary">
      <div class="stat-value">${stats.confirmados}</div>
      <div class="stat-label">Confirmados</div>
    </div>
    <div class="stat-card secondary">
      <div class="stat-value">${stats.pendientes}</div>
      <div class="stat-label">Pendientes</div>
    </div>
  </div>

  <table>
    <thead>
      <tr>
        ${tableHeaders}
      </tr>
    </thead>
    <tbody>
      ${filteredEvents.map((event, idx) => {
        const servicios = event.servicios_contratados?.filter(s => s.contratado).map(s => s.servicio) || [];
        const asisList = getAsistentes(event);
        const asisHtml = asisList.length ? `<div style="font-size:10px;color:#64748b;margin-top:2px">+ ${asisList.join(', ')}</div>` : '';
        if (hideFinancials) {
          return `
          <tr>
            <td>${idx + 1}</td>
            <td>
              <div class="event-name">${event.nombre_novios || 'Sin nombre'}</div>
            </td>
            <td>${formatDate(event.fecha_evento)}</td>
            <td>${event.lugar_evento || '-'}</td>
            <td>${event.nombre_dj || '-'}${asisHtml}</td>
            <td>
              <span class="status ${event.estado || ''}">${event.estado || 'Sin estado'}</span>
            </td>
            <td>
              <div class="event-services">${servicios.join(', ') || '-'}</div>
            </td>
          </tr>
          `;
        }
        return `
        <tr>
          <td>${idx + 1}</td>
          <td>
            <div class="event-name">${event.nombre_novios || 'Sin nombre'}</div>
          </td>
          <td>${formatDate(event.fecha_evento)}</td>
          <td>${event.lugar_evento || '-'}</td>
          <td>${event.nombre_dj || '-'}${asisHtml}</td>
          <td>${formatCurrency(getEffectiveCache(event))}</td>
          <td>
            <span class="status ${event.estado || ''}">${event.estado || 'Sin estado'}</span>
          </td>
          <td>
            <div class="event-services">${servicios.join(', ') || '-'}</div>
          </td>
        </tr>
        `;
      }).join('')}
    </tbody>
  </table>

  <div class="footer">
    <strong>Luis Herrera Eventos</strong> • Informe generado automáticamente<br>
    Total: ${stats.total} eventos${!hideFinancials ? ` • Facturación: ${formatCurrency(stats.totalCache)}` : ''}
  </div>

  <script>
    window.onload = function() { window.print(); }
  </script>
</body>
</html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    toast.success('Preparando documento para imprimir...');
  };

  // Renderizar tarjeta de evento
  const renderEventCard = (event) => {
    const servicios = event.servicios_contratados?.filter(s => s.contratado) || [];
    const isPast = event.fecha_evento && new Date(event.fecha_evento) < new Date();
    
    // Rol del DJ filtrado en este evento (titular / asistente 1 / asistente 2)
    let djRoleBadge = null;
    if (selectedDJ) {
      let role = null;
      if (event.nombre_dj === selectedDJ) role = 'Titular';
      else if (event.nombre_asistente === selectedDJ) role = 'Asistente 1';
      else if (event.nombre_asistente2 === selectedDJ) role = 'Asistente 2';
      if (role) {
        const colors = role === 'Titular'
          ? 'bg-emerald-900/40 text-emerald-300 border-emerald-500/30'
          : 'bg-amber-900/30 text-amber-300 border-amber-500/30';
        djRoleBadge = (
          <Badge variant="outline" className={`${colors} text-[10px] px-2 py-0`} data-testid={`dj-role-${event.id}`}>
            {role}
          </Badge>
        );
      }
    }

    return (
      <div 
        key={event.id}
        className={`bg-slate-900/50 p-4 rounded-lg cursor-pointer hover:bg-slate-800 transition-all border-l-4 ${
          event.estado === 'confirmado' ? 'border-green-500' :
          event.estado === 'pendiente' ? 'border-yellow-500' :
          event.estado === 'cancelado' ? 'border-red-500' :
          'border-slate-600'
        } ${isPast ? 'opacity-70' : ''}`}
        onClick={() => onSelectEvent && onSelectEvent(event)}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="text-white font-semibold truncate">{event.nombre_novios}</h4>
              {djRoleBadge}
              {isPast && (
                <Badge variant="outline" className="text-slate-400 border-slate-600 text-xs">
                  Pasado
                </Badge>
              )}
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-sm text-slate-400">
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                <span>{formatDate(event.fecha_evento)}</span>
              </div>
              <div className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                <span className="truncate">{event.lugar_evento || 'Sin ubicación'}</span>
              </div>
              {event.nombre_dj && (
                <div className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  <span>DJ: {event.nombre_dj}</span>
                </div>
              )}
              {getAsistentes(event).length > 0 && (
                <div className="flex items-center gap-1" data-testid={`event-asistentes-${event.id}`}>
                  <Users className="w-3 h-3 text-amber-400" />
                  <span className="truncate">Asist.: {getAsistentes(event).join(', ')}</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <DollarSign className="w-3 h-3" />
                <span>{formatCurrency(getEffectiveCache(event))}</span>
              </div>
            </div>

            {servicios.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {servicios.slice(0, 4).map((s, idx) => (
                  <Badge key={idx} variant="secondary" className="bg-cyan-900/30 text-cyan-300 text-xs">
                    {s.servicio}
                  </Badge>
                ))}
                {servicios.length > 4 && (
                  <Badge variant="secondary" className="bg-slate-700 text-slate-300 text-xs">
                    +{servicios.length - 4} más
                  </Badge>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col items-end gap-1">
            <Badge className={`text-xs ${
              event.estado === 'confirmado' ? 'bg-green-600/30 text-green-300 border-green-500/30' :
              event.estado === 'pendiente' ? 'bg-yellow-600/30 text-yellow-300 border-yellow-500/30' :
              event.estado === 'cancelado' ? 'bg-red-600/30 text-red-300 border-red-500/30' :
              'bg-slate-700 text-slate-300'
            }`}>
              {event.estado || 'Sin estado'}
            </Badge>
            {event.tipo_evento && (
              <span className="text-xs text-slate-500">{event.tipo_evento}</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-cyan-400" />
            Centro de Informes
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Genera informes detallados de eventos, DJs, venues y servicios
          </p>
        </div>

        {/* Botones de exportación */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Toggle para ocultar info económica */}
          <div className="flex items-center gap-2 bg-slate-800/50 px-3 py-2 rounded-lg border border-slate-700">
            <Switch
              id="hide-financials"
              checked={hideFinancials}
              onCheckedChange={setHideFinancials}
              data-testid="hide-financials-toggle"
            />
            <Label 
              htmlFor="hide-financials" 
              className="text-slate-300 text-sm cursor-pointer flex items-center gap-1"
            >
              {hideFinancials ? <EyeOff className="w-4 h-4 text-orange-400" /> : <DollarSign className="w-4 h-4 text-emerald-400" />}
              <span className="hidden sm:inline">{hideFinancials ? 'Info económica oculta' : 'Mostrar economía'}</span>
            </Label>
          </div>
          
          <Button
            onClick={printReport}
            className="bg-slate-700 hover:bg-slate-600 text-white"
            size="sm"
            data-testid="print-report-btn"
          >
            <Printer className="w-4 h-4 mr-1" />
            Imprimir
          </Button>
          <Button
            onClick={shareWhatsApp}
            className="bg-green-600 hover:bg-green-700 text-white"
            size="sm"
            data-testid="share-whatsapp-btn"
          >
            <MessageCircle className="w-4 h-4 mr-1" />
            WhatsApp
          </Button>
          <Button
            onClick={sendEmail}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            size="sm"
            data-testid="share-email-btn"
          >
            <Mail className="w-4 h-4 mr-1" />
            Email
          </Button>
        </div>
      </div>

      {/* Tabs de tipos de informe */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800 border-slate-700 flex-wrap h-auto p-1">
          <TabsTrigger value="general" className="data-[state=active]:bg-cyan-600">
            <FileText className="w-4 h-4 mr-1" />
            General
          </TabsTrigger>
          <TabsTrigger value="dj" className="data-[state=active]:bg-cyan-600">
            <User className="w-4 h-4 mr-1" />
            Por DJ
          </TabsTrigger>
          <TabsTrigger value="venue" className="data-[state=active]:bg-cyan-600">
            <Building2 className="w-4 h-4 mr-1" />
            Por Venue
          </TabsTrigger>
          <TabsTrigger value="servicio" className="data-[state=active]:bg-cyan-600">
            <Music className="w-4 h-4 mr-1" />
            Por Servicio
          </TabsTrigger>
          <TabsTrigger value="facturacion" className="data-[state=active]:bg-cyan-600">
            <DollarSign className="w-4 h-4 mr-1" />
            Facturación
          </TabsTrigger>
          <TabsTrigger value="agenda" className="data-[state=active]:bg-cyan-600">
            <Clock className="w-4 h-4 mr-1" />
            Agenda
          </TabsTrigger>
        </TabsList>

        {/* Filtros Comunes */}
        <Card className="bg-slate-800 border-slate-700 mt-4">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Filter className="w-5 h-5 text-cyan-400" />
                Filtros
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-slate-400 hover:text-white"
              >
                Limpiar filtros
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Primera fila de filtros */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Búsqueda general */}
              <div className="space-y-1">
                <Label className="text-slate-300 text-sm">Búsqueda</Label>
                <Input
                  placeholder="Buscar evento, lugar, DJ..."
                  className="bg-slate-900 border-slate-700 text-white"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Filtro DJ */}
              <div className="space-y-1">
                <Label className="text-slate-300 text-sm">DJ</Label>
                <Select value={selectedDJ || "all"} onValueChange={(v) => setSelectedDJ(v === "all" ? "" : v)}>
                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                    <SelectValue placeholder="Todos los DJs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los DJs</SelectItem>
                    {uniqueDJs.map(dj => (
                      <SelectItem key={dj} value={dj}>{dj}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Filtro Venue */}
              <div className="space-y-1">
                <Label className="text-slate-300 text-sm">Venue / Lugar</Label>
                <Select value={selectedVenue || "all"} onValueChange={(v) => setSelectedVenue(v === "all" ? "" : v)}>
                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                    <SelectValue placeholder="Todos los lugares" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los lugares</SelectItem>
                    {uniqueVenues.map(venue => (
                      <SelectItem key={venue} value={venue}>{venue}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Filtro Servicio */}
              <div className="space-y-1">
                <Label className="text-slate-300 text-sm">Servicio</Label>
                <Select value={selectedService || "all"} onValueChange={(v) => setSelectedService(v === "all" ? "" : v)}>
                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                    <SelectValue placeholder="Todos los servicios" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los servicios</SelectItem>
                    {uniqueServices.map(service => (
                      <SelectItem key={service} value={service}>{service}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Segunda fila - Fechas y opciones */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
              {/* Fecha desde */}
              <div className="space-y-1">
                <Label className="text-slate-300 text-sm">Desde</Label>
                <Input
                  type="date"
                  className="bg-slate-900 border-slate-700 text-white"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </div>

              {/* Fecha hasta */}
              <div className="space-y-1">
                <Label className="text-slate-300 text-sm">Hasta</Label>
                <Input
                  type="date"
                  className="bg-slate-900 border-slate-700 text-white"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </div>

              {/* Ocultar pasados */}
              <div className="flex items-center gap-3">
                <Switch
                  id="hide-past"
                  checked={hidePastEvents}
                  onCheckedChange={setHidePastEvents}
                />
                <Label htmlFor="hide-past" className="text-slate-300 text-sm cursor-pointer">
                  {hidePastEvents ? <EyeOff className="w-4 h-4 inline mr-1" /> : <Eye className="w-4 h-4 inline mr-1" />}
                  Ocultar pasados
                </Label>
              </div>

              {/* Ordenación */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  {sortOrder === 'desc' ? (
                    <>
                      <SortDesc className="w-4 h-4 mr-1" />
                      Más recientes
                    </>
                  ) : (
                    <>
                      <SortAsc className="w-4 h-4 mr-1" />
                      Más antiguos
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Resumen de estadísticas */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-4">
          <Card className="bg-gradient-to-br from-cyan-600/20 to-cyan-800/20 border-cyan-700/50">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-cyan-300">{stats.total}</div>
              <div className="text-xs text-slate-400">Total Eventos</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-600/20 to-emerald-800/20 border-emerald-700/50">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-emerald-300">{formatCurrency(stats.totalCache)}</div>
              <div className="text-xs text-slate-400">Facturación</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-600/20 to-green-800/20 border-green-700/50">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-300">{stats.confirmados}</div>
              <div className="text-xs text-slate-400">Confirmados</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-yellow-600/20 to-yellow-800/20 border-yellow-700/50">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-yellow-300">{stats.pendientes}</div>
              <div className="text-xs text-slate-400">Pendientes</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-600/20 to-blue-800/20 border-blue-700/50">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-blue-300">{stats.futuros}</div>
              <div className="text-xs text-slate-400">Futuros</div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-slate-600/20 to-slate-800/20 border-slate-700/50">
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-slate-300">{stats.pasados}</div>
              <div className="text-xs text-slate-400">Pasados</div>
            </CardContent>
          </Card>
        </div>

        {/* Contenido de tabs */}
        <TabsContent value="general" className="mt-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">
                  Resultados ({filteredEvents.length} eventos)
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setExpandedResults(!expandedResults)}
                  className="text-slate-400"
                >
                  {expandedResults ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
              </div>
            </CardHeader>
            {expandedResults && (
              <CardContent className="space-y-3 max-h-[600px] overflow-y-auto">
                {filteredEvents.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg">No se encontraron eventos</p>
                    <p className="text-sm">Ajusta los filtros para ver resultados</p>
                  </div>
                ) : (
                  filteredEvents.map(event => renderEventCard(event))
                )}
              </CardContent>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="dj" className="mt-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <User className="w-5 h-5 text-cyan-400" />
                Informe por DJ
                {selectedDJ && <Badge className="bg-cyan-600">{selectedDJ}</Badge>}
              </CardTitle>
              <p className="text-slate-400 text-sm">
                Resumen del equipo (titular vs asistente). Selecciona un DJ en los filtros o toca una persona para ver su detalle.
              </p>
            </CardHeader>
            <CardContent className="space-y-3 max-h-[600px] overflow-y-auto">
              {!selectedDJ ? (
                teamStats.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg">No hay datos del equipo</p>
                    <p className="text-sm">Ajusta los filtros para ver el resumen</p>
                  </div>
                ) : (
                  <div data-testid="team-summary">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-white font-semibold">Resumen del equipo</h4>
                      <span className="text-xs text-slate-400">{teamStats.length} personas · {stats.total} eventos</span>
                    </div>
                    <p className="text-xs text-slate-500 mb-3">Toca una persona para ver su detalle. "Titular" = DJ principal · "Asist." = asistente.</p>
                    <div className="space-y-2">
                      {teamStats.map(member => (
                        <button
                          key={member.name}
                          onClick={() => setSelectedDJ(member.name)}
                          data-testid={`team-row-${member.name}`}
                          className="w-full text-left bg-slate-900/60 hover:bg-slate-700/60 border border-slate-700 rounded-lg p-3 transition-colors"
                        >
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2 min-w-0">
                              <User className="w-4 h-4 text-cyan-400 shrink-0" />
                              <span className="text-white font-medium truncate">{member.name}</span>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className="bg-cyan-600/80 text-white">Titular: {member.titular}</Badge>
                              <Badge className="bg-amber-600/80 text-white">Asist.: {member.asistente}</Badge>
                              <Badge variant="outline" className="border-slate-500 text-slate-200">Total: {member.total}</Badge>
                              {!hideFinancials && member.facturacionTitular > 0 && (
                                <Badge className="bg-emerald-700/80 text-white">{formatCurrency(member.facturacionTitular)}</Badge>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )
              ) : (
                <>
                  {selectedDJRoleStats && (
                    <div className="flex items-center gap-2 flex-wrap mb-2" data-testid="dj-role-breakdown">
                      <Badge className="bg-cyan-600/80 text-white">Como titular: {selectedDJRoleStats.titular}</Badge>
                      <Badge className="bg-amber-600/80 text-white">Como asistente: {selectedDJRoleStats.asistente}</Badge>
                      <Badge variant="outline" className="border-slate-500 text-slate-200">Total: {selectedDJRoleStats.total}</Badge>
                    </div>
                  )}
                  {filteredEvents.length === 0 ? (
                    <div className="text-center py-12 text-slate-400">
                      <p>No hay eventos para {selectedDJ} en el período seleccionado</p>
                    </div>
                  ) : (
                    filteredEvents.map(event => renderEventCard(event))
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="venue" className="mt-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Building2 className="w-5 h-5 text-cyan-400" />
                Informe por Venue
                {selectedVenue && <Badge className="bg-cyan-600">{selectedVenue}</Badge>}
              </CardTitle>
              <p className="text-slate-400 text-sm">
                Selecciona un lugar en los filtros para ver el historial de eventos
              </p>
            </CardHeader>
            <CardContent className="space-y-3 max-h-[600px] overflow-y-auto">
              {!selectedVenue ? (
                <div className="text-center py-12 text-slate-400">
                  <Building2 className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg">Selecciona un Venue</p>
                  <p className="text-sm">Usa el filtro "Venue / Lugar" para generar el informe</p>
                </div>
              ) : filteredEvents.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <p>No hay eventos en {selectedVenue} en el período seleccionado</p>
                </div>
              ) : (
                filteredEvents.map(event => renderEventCard(event))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="servicio" className="mt-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Music className="w-5 h-5 text-cyan-400" />
                Informe por Servicio
                {selectedService && <Badge className="bg-cyan-600">{selectedService}</Badge>}
              </CardTitle>
              <p className="text-slate-400 text-sm">
                Selecciona un servicio para ver qué eventos lo han contratado
              </p>
            </CardHeader>
            <CardContent>
              {/* Top servicios */}
              {!selectedService && stats.topServicios.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-white font-semibold mb-3">🏆 Servicios más contratados</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {stats.topServicios.map(([servicio, count], idx) => (
                      <div 
                        key={servicio}
                        className="flex items-center justify-between bg-slate-900/50 p-3 rounded-lg cursor-pointer hover:bg-slate-700 transition-colors"
                        onClick={() => setSelectedService(servicio)}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : '•'}</span>
                          <span className="text-slate-200">{servicio}</span>
                        </div>
                        <Badge className="bg-cyan-600">{count} eventos</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {!selectedService ? (
                  <div className="text-center py-8 text-slate-400">
                    <Music className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p className="text-lg">Selecciona un Servicio</p>
                    <p className="text-sm">Usa el filtro "Servicio" o haz clic en uno de arriba</p>
                  </div>
                ) : filteredEvents.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <p>No hay eventos con el servicio "{selectedService}" en el período seleccionado</p>
                  </div>
                ) : (
                  filteredEvents.map(event => renderEventCard(event))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="facturacion" className="mt-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-400" />
                Informe de Facturación
              </CardTitle>
              <p className="text-slate-400 text-sm">
                Resumen económico del período seleccionado
              </p>
            </CardHeader>
            <CardContent>
              {/* Resumen de facturación */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <Card className="bg-emerald-900/30 border-emerald-700/50">
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-emerald-300">{formatCurrency(stats.totalCache)}</div>
                    <div className="text-sm text-slate-400">Facturación Total</div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-900/50 border-slate-700">
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-slate-300">
                      {stats.total > 0 ? formatCurrency(stats.totalCache / stats.total) : '-'}
                    </div>
                    <div className="text-sm text-slate-400">Promedio por Evento</div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-900/50 border-slate-700">
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-slate-300">{stats.total}</div>
                    <div className="text-sm text-slate-400">Total Eventos</div>
                  </CardContent>
                </Card>
              </div>

              {/* Lista de eventos con caché */}
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {filteredEvents
                  .filter(e => getEffectiveCache(e) > 0)
                  .sort((a, b) => getEffectiveCache(b) - getEffectiveCache(a))
                  .map(event => (
                    <div 
                      key={event.id}
                      className="flex items-center justify-between bg-slate-900/50 p-3 rounded-lg hover:bg-slate-700 cursor-pointer"
                      onClick={() => onSelectEvent && onSelectEvent(event)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-medium truncate">{event.nombre_novios}</div>
                        <div className="text-xs text-slate-400">
                          {formatDate(event.fecha_evento)} • {event.lugar_evento || 'Sin lugar'}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-emerald-400 font-bold">{formatCurrency(getEffectiveCache(event))}</div>
                        {event.nombre_dj && <div className="text-xs text-slate-500">{event.nombre_dj}</div>}
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="agenda" className="mt-4">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-400" />
                Agenda de Eventos
              </CardTitle>
              <p className="text-slate-400 text-sm">
                Próximos eventos y trabajos programados
              </p>
            </CardHeader>
            <CardContent className="space-y-3 max-h-[600px] overflow-y-auto">
              {filteredEvents
                .filter(e => !hidePastEvents || (e.fecha_evento && new Date(e.fecha_evento) >= new Date()))
                .map(event => renderEventCard(event))}
              
              {filteredEvents.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                  <Calendar className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg">No hay eventos en la agenda</p>
                  <p className="text-sm">Ajusta los filtros o el período de fechas</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default InformesView;
